<!--抽签界面 - 512人迷宫入位-->
<template>
  <div class="draw-lottery-container">
    <!-- 主内容区 - 左右布局 -->
    <div class="main-content">
      <!-- 左侧：游戏区域 -->
      <div class="left-panel">
        <!-- 分类选择 -->
        <div class="category-selector">
          <el-select
            v-model="currentCategory"
            placeholder="请选择分类"
            @change="handleCategoryChange"
            class="category-select"
          >
            <el-option
              v-for="item in categoryOptions"
              :key="item.value"
              :label="item.label"
              :value="item.value"
            >
            </el-option>
          </el-select>
          <div class="category-info">
            <span class="info-text">当前：{{ getCurrentCategoryLabel() }}</span>
            <span class="info-text">共{{ totalPersons }}人</span>
          </div>
          <el-button
            class="exit-button"
            type="danger"
            @click="handleExit"
            size="small"
          >
            退出
          </el-button>
        </div>

        <div class="maze-container">
          <!-- 使用 el-row 和 el-col 布局 -->
          <el-row :gutter="2" v-for="rowIndex in totalRows" :key="rowIndex">
            <el-col :span="2" v-for="colIndex in cellsPerRow" :key="colIndex">
              <div
                v-if="getCellByRowCol(rowIndex, colIndex)"
                class="path-cell"
                :class="{
                  'is-occupied':
                    getCellByRowCol(rowIndex, colIndex).person !== null,
                  'is-start': getCellIndex(rowIndex, colIndex) === 0,
                  'is-end': getCellIndex(rowIndex, colIndex) === 511
                }"
              >
                <!-- 格子编号 -->
                <div class="cell-number">
                  {{ getCellIndex(rowIndex, colIndex) + 1 }}
                </div>

                <!-- 已入位的人 -->
                <div
                  v-if="getCellByRowCol(rowIndex, colIndex).person"
                  class="person-in-cell"
                >
                  <div class="person-id">
                    {{ getCellByRowCol(rowIndex, colIndex).person.id }}
                  </div>
                  <div class="person-name-small">
                    {{ getCellByRowCol(rowIndex, colIndex).person.name }}
                  </div>
                </div>
              </div>
            </el-col>
          </el-row>

          <!-- 动画中的人物 -->
          <div
            v-if="animatingPerson"
            class="animating-person"
            :style="animatingPersonStyle"
          >
            <div class="person-avatar-large">{{ animatingPerson.id }}</div>
            <div class="person-name">{{ animatingPerson.name }}</div>
          </div>
        </div>
      </div>

      <!-- 右侧：控制面板 -->
      <div class="right-panel">
        <!-- 头部统计 -->
        <div class="header-compact">
          <h2>🎲 抽签入位 (512人)</h2>
          <div class="stats-compact">
            <span class="stat-badge"
              >已入位: <strong>{{ placedCount }}</strong> / 512</span
            >
            <span class="stat-badge"
              >剩余: <strong>{{ remainingCount }}</strong></span
            >
          </div>
        </div>

        <!-- 控制按钮 -->
        <div class="controls-compact">
          <el-button
            class="btn-compact btn-draw"
            @click="drawOne"
            :disabled="isAnimating || remainingCount === 0"
            :loading="drawLoading"
            type="primary"
          >
            <span v-if="remainingCount > 0">🎯 抽签</span>
            <span v-else>✅ 完成</span>
          </el-button>
          <el-button
            class="btn-compact btn-auto"
            @click="toggleAuto"
            :disabled="remainingCount === 0"
            :loading="autoLoading"
            type="success"
          >
            <span v-if="!isAutoMode">⚡ 自动</span>
            <span v-else>⏸️ 暂停</span>
          </el-button>
          <el-button
            class="btn-compact btn-save"
            @click="saveResult"
            :loading="saveLoading"
            type="warning"
          >
            💾 保存
          </el-button>
        </div>

        <!-- 动画开关 -->
        <div class="animation-toggle">
          <label class="toggle-label">
            <input
              type="checkbox"
              v-model="enableAnimation"
              class="toggle-checkbox"
            />
            <span class="toggle-slider"></span>
            <span class="toggle-text">
              {{ enableAnimation ? "🎬 动画已开启" : "⏩ 快速模式" }}
            </span>
          </label>
        </div>

        <!-- 当前抽中的人 -->
        <div class="current-draw-compact">
          <div class="draw-card-compact" :class="{ 'is-rolling': isRolling }">
            <div class="card-header-compact">
              <span v-if="!currentPerson && !isRolling">⏳ 未开始</span>
              <span v-else-if="isRolling">🎰 抽签中...</span>
              <span v-else>🎊 抽中</span>
            </div>
            <div class="card-body-compact">
              <template v-if="!currentPerson && !isRolling">
                <div class="person-avatar-compact empty">?</div>
                <div class="person-info-compact">
                  <div class="person-name-compact">点击开始抽签</div>
                  <div class="person-position-compact">等待中</div>
                </div>
              </template>
              <template v-else-if="isRolling">
                <div class="person-avatar-compact rolling">
                  {{ rollingPerson.id }}
                </div>
                <div class="person-info-compact">
                  <div class="person-name-compact rolling-text">
                    {{ rollingPerson.name }}
                  </div>
                  <div class="person-position-compact rolling-text">
                    位置: {{ rollingPerson.position }}
                  </div>
                </div>
              </template>
              <template v-else>
                <div class="person-avatar-compact">{{ currentPerson.id }}</div>
                <div class="person-info-compact">
                  <div class="person-name-compact">
                    {{ currentPerson.name }}
                  </div>
                  <div class="person-position-compact">
                    位置: {{ currentPerson.targetPosition + 1 }}
                  </div>
                </div>
              </template>
            </div>
          </div>
        </div>

        <!-- 待抽签人员列表 -->
        <div class="waiting-list-compact">
          <h3>待抽签 ({{ remainingCount }}人)</h3>
          <div class="person-chips-compact">
            <div
              v-for="person in waitingPersons.slice(0, 20)"
              :key="person.id"
              class="person-chip-compact"
            >
              {{ person.id }}
            </div>
            <div v-if="remainingCount > 20" class="more-indicator">
              +{{ remainingCount - 20 }}
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: "DrawLottery",
  data() {
    return {
      // 128个人员数据
      persons: [],
      // 128个路径格子
      pathCells: [],
      // 当前正在入位的人
      currentPerson: null,
      // 动画中的人
      animatingPerson: null,
      // 动画状态
      isAnimating: false,
      // 自动模式
      isAutoMode: false,
      autoTimer: null,
      // 动画位置
      animatingPersonStyle: {},
      // 跑马灯状态
      isRolling: false,
      // 跑马灯显示的人
      rollingPerson: { id: 0, name: "", position: 0 },
      // 跑马灯定时器
      rollingTimer: null,
      // 动画开关
      enableAnimation: true,
      // 网格布局配置
      cellsPerRow: 12, // 每行12个格子
      totalRows: 43, // 总共43行 (512/12 ≈ 42.67)
      // Loading状态
      drawLoading: false, // 抽签按钮loading
      autoLoading: false, // 自动按钮loading
      saveLoading: false, // 保存按钮loading
      // 分类相关
      currentCategory: "male_single", // 当前选中的分类
      categoryOptions: [
        { value: "male_single", label: "男单", persons: 512 },
        { value: "female_single", label: "女单", persons: 512 },
        { value: "male_double", label: "男双", persons: 256 },
        { value: "female_double", label: "女双", persons: 256 },
        { value: "mixed_double", label: "混双", persons: 256 }
      ]
    };
  },
  computed: {
    // 已入位人数
    placedCount() {
      return this.persons.filter(p => p.placed).length;
    },
    // 剩余人数
    remainingCount() {
      return this.persons.filter(p => !p.placed).length;
    },
    // 待抽签人员
    waitingPersons() {
      return this.persons.filter(p => !p.placed);
    },
    // 当前分类总人数
    totalPersons() {
      const category = this.categoryOptions.find(
        c => c.value === this.currentCategory
      );
      return category ? category.persons : 512;
    }
  },
  created() {
    this.initData();
  },
  beforeDestroy() {
    if (this.autoTimer) {
      clearInterval(this.autoTimer);
    }
    if (this.rollingTimer) {
      clearInterval(this.rollingTimer);
    }
  },
  methods: {
    // 获取当前分类标签
    getCurrentCategoryLabel() {
      const category = this.categoryOptions.find(
        c => c.value === this.currentCategory
      );
      return category ? category.label : "未知";
    },

    // 处理分类切换
    handleCategoryChange(value) {
      console.log("切换分类：", value);

      // 停止自动模式
      if (this.isAutoMode) {
        this.isAutoMode = false;
        this.autoLoading = false;
      }

      // 重置所有状态
      this.currentPerson = null;
      this.animatingPerson = null;
      this.isAnimating = false;
      this.isRolling = false;
      this.rollingPerson = { id: 0, name: "", position: 0 };
      this.drawLoading = false;
      this.autoLoading = false;

      // 根据新分类重新初始化数据
      this.initData();

      // 滚动到顶部
      this.$nextTick(() => {
        const mazeContainer = document.querySelector(".maze-container");
        if (mazeContainer) {
          mazeContainer.scrollTo({
            top: 0,
            behavior: "smooth"
          });
        }
      });

      this.$message.success(`已切换到：${this.getCurrentCategoryLabel()}`);
    },

    // 退出抽签界面
    handleExit() {
      this.$confirm('确定要退出抽签界面吗？', '提示', {
        confirmButtonText: '确定',
        cancelButtonText: '取消',
        type: 'warning'
      }).then(() => {
        // 返回上一页或指定页面
        this.$router.go(-1);
        // 或者指定跳转到某个页面：
        // this.$router.push('/newsGlIndex');
      }).catch(() => {
        // 取消退出
      });
    },

    // 初始化数据
    initData() {
      // 获取当前分类的人数
      const totalCount = this.totalPersons;

      // 生成人员
      this.persons = [];
      const names = [
        "张三",
        "李四",
        "王五",
        "赵六",
        "钱七",
        "孙八",
        "周九",
        "吴十"
      ];
      for (let i = 1; i <= totalCount; i++) {
        this.persons.push({
          id: i,
          name: `${names[(i - 1) % names.length]}${Math.ceil(i / 8)}`,
          placed: false,
          position: null
        });
      }

      // 初始化路径格子
      this.pathCells = [];
      for (let i = 0; i < totalCount; i++) {
        this.pathCells.push({
          person: null
        });
      }

      // 更新总行数
      this.totalRows = Math.ceil(totalCount / this.cellsPerRow);
    },

    // 根据行列获取格子索引
    getCellIndex(rowIndex, colIndex) {
      return (rowIndex - 1) * this.cellsPerRow + (colIndex - 1);
    },

    // 根据行列获取格子对象
    getCellByRowCol(rowIndex, colIndex) {
      const index = this.getCellIndex(rowIndex, colIndex);
      if (index >= 0 && index < 512) {
        return this.pathCells[index];
      }
      return null;
    },

    // 抽签一个人
    drawOne() {
      if (this.isAnimating || this.remainingCount === 0) return;

      // 开启loading
      this.drawLoading = true;

      // 随机选择一个空位置
      const emptyPositions = [];
      for (let i = 0; i < this.pathCells.length; i++) {
        if (this.pathCells[i].person === null) {
          emptyPositions.push(i);
        }
      }

      if (emptyPositions.length === 0) {
        this.drawLoading = false;
        return;
      }

      // 从所有空位中随机选择一个
      const randomIndex = Math.floor(Math.random() * emptyPositions.length);
      const targetPosition = emptyPositions[randomIndex];
      alert(JSON.stringify(targetPosition))
      if (this.enableAnimation) {
        // 开启动画：跑马灯效果
        this.startRolling();
        // 0.8秒后停止跑马灯，显示最终抽中的人
        setTimeout(() => {
          this.stopRolling(targetPosition);
        }, 800);
      } else {
        // 关闭动画：直接入位
        this.quickDraw(targetPosition);
      }
    },

    // 快速抽签（无动画）
    quickDraw(targetPosition) {
      this.isAnimating = true;

      // 随机选择一个未抽签的人
      const waiting = this.persons.filter(p => !p.placed);
      const randomIndex = Math.floor(Math.random() * waiting.length);
      const selectedPerson = waiting[randomIndex];

      // 设置当前抽中的人
      this.currentPerson = {
        ...selectedPerson,
        targetPosition
      };

      // 直接放置，不播放动画
      this.placePerson(selectedPerson, targetPosition);
      this.isAnimating = false;

      // 如果是自动模式，继续抽签（增加延迟以便看到滚动效果）
      if (this.isAutoMode && this.remainingCount > 0) {
        setTimeout(() => {
          this.drawOne();
        }, 300);
      }
    },

    // 开始跑马灯
    startRolling() {
      this.isRolling = true;
      this.isAnimating = true;

      const waiting = this.persons.filter(p => !p.placed);
      let rollCount = 0;
      const maxRolls = 30; // 最多滚动30次

      // 跑马灯效果：快速切换显示不同的人
      this.rollingTimer = setInterval(() => {
        const randomIndex = Math.floor(Math.random() * waiting.length);
        const randomPerson = waiting[randomIndex];
        const randomPosition = Math.floor(Math.random() * 512) + 1;

        this.rollingPerson = {
          id: randomPerson.id,
          name: randomPerson.name,
          position: randomPosition
        };

        rollCount++;
        // 逐渐减慢滚动速度
        if (rollCount > maxRolls * 0.7) {
          clearInterval(this.rollingTimer);
          // 后期慢速滚动
          this.rollingTimer = setInterval(() => {
            const randomIndex = Math.floor(Math.random() * waiting.length);
            const randomPerson = waiting[randomIndex];
            const randomPosition = Math.floor(Math.random() * 512) + 1;

            this.rollingPerson = {
              id: randomPerson.id,
              name: randomPerson.name,
              position: randomPosition
            };
          }, 150);
        }
      }, 50);
    },

    // 停止跑马灯
    stopRolling(targetPosition) {
      clearInterval(this.rollingTimer);
      this.isRolling = false;

      // 随机选择一个未抽签的人
      const waiting = this.persons.filter(p => !p.placed);
      const randomIndex = Math.floor(Math.random() * waiting.length);
      const selectedPerson = waiting[randomIndex];

      // 设置当前抽中的人
      this.currentPerson = {
        ...selectedPerson,
        targetPosition
      };

      // 开始动画
      setTimeout(() => {
        this.animatePerson(selectedPerson, targetPosition);
      }, 150);
    },

    // 动画移动人物
    animatePerson(person, targetPosition) {
      this.isAnimating = true;
      this.animatingPerson = person;

      // 起始位置（屏幕中央上方）
      const startX = window.innerWidth / 2 - 50;
      const startY = 100;

      // 计算目标格子的行列位置
      const rowIndex = Math.floor(targetPosition / this.cellsPerRow) + 1;
      const colIndex = (targetPosition % this.cellsPerRow) + 1;

      // 查找目标格子的 DOM 元素
      this.$nextTick(() => {
        const mazeContainer = document.querySelector(".maze-container");
        const allCells = mazeContainer.querySelectorAll(".path-cell");
        const targetCell = allCells[targetPosition];

        if (!targetCell) {
          // 如果找不到目标格子，直接放置
          this.placePerson(person, targetPosition);
          this.animatingPerson = null;
          this.isAnimating = false;
          return;
        }

        const cellRect = targetCell.getBoundingClientRect();
        const targetX = cellRect.left + cellRect.width / 2 - 50;
        const targetY =
          cellRect.top + cellRect.height / 2 - 50 + window.scrollY;

        // 设置起始位置
        this.animatingPersonStyle = {
          left: `${startX}px`,
          top: `${startY}px`,
          opacity: 0,
          transform: "scale(0.5) rotate(0deg)"
        };

        // 延迟开始动画
        setTimeout(() => {
          this.animatingPersonStyle = {
            left: `${targetX}px`,
            top: `${targetY}px`,
            opacity: 1,
            transform: "scale(1) rotate(360deg)",
            transition: "all 0.6s cubic-bezier(0.68, -0.55, 0.265, 1.55)"
          };

          // 动画结束后放置人物
          setTimeout(() => {
            this.placePerson(person, targetPosition);
            this.animatingPerson = null;
            this.isAnimating = false;

            // 如果是自动模式，继续抽签（增加延迟以便看到滚动效果）
            if (this.isAutoMode && this.remainingCount > 0) {
              setTimeout(() => {
                this.drawOne();
              }, 500);
            }
          }, 600);
        }, 50);
      });
    },

    // 放置人物到格子
    placePerson(person, position) {
      person.placed = true;
      person.position = position;
      this.pathCells[position].person = person;

      // 自动滚动到目标位置
      this.scrollToPosition(position);

      // 关闭loading（延迟一点以确保用户看到loading效果）
      setTimeout(() => {
        this.drawLoading = false;
        this.autoLoading = false;
      }, 200);

      // 不清除 currentPerson，保持显示最后抽中的人
    },

    // 滚动到指定位置
    scrollToPosition(position) {
      this.$nextTick(() => {
        const mazeContainer = document.querySelector(".maze-container");
        if (!mazeContainer) return;

        const allCells = mazeContainer.querySelectorAll(".path-cell");
        const targetCell = allCells[position];

        if (targetCell) {
          // 计算目标格子相对于容器的位置
          const containerRect = mazeContainer.getBoundingClientRect();
          const cellRect = targetCell.getBoundingClientRect();

          // 计算需要滚动的距离
          const scrollTop =
            cellRect.top -
            containerRect.top +
            mazeContainer.scrollTop -
            containerRect.height / 2 +
            cellRect.height / 2;

          // 平滑滚动到目标位置
          mazeContainer.scrollTo({
            top: scrollTop,
            behavior: "smooth"
          });
        }
      });
    },

    // 切换自动模式
    toggleAuto() {
      this.isAutoMode = !this.isAutoMode;

      if (this.isAutoMode) {
        // 开启自动模式loading
        this.autoLoading = true;
        this.drawOne();
      } else {
        // 暂停自动模式，关闭loading
        this.autoLoading = false;
      }
    },

    // 重置
    reset() {
      if (this.autoTimer) {
        clearInterval(this.autoTimer);
      }
      if (this.rollingTimer) {
        clearInterval(this.rollingTimer);
      }
      this.isAutoMode = false;
      this.currentPerson = null;
      this.animatingPerson = null;
      this.isAnimating = false;
      this.isRolling = false;
      this.rollingPerson = { id: 0, name: "", position: 0 };
      // 重置loading状态
      this.drawLoading = false;
      this.autoLoading = false;
      this.initData();

      // 滚动到顶部
      this.$nextTick(() => {
        const mazeContainer = document.querySelector(".maze-container");
        if (mazeContainer) {
          mazeContainer.scrollTo({
            top: 0,
            behavior: "smooth"
          });
        }
      });
    },

    // 保存结果
    saveResult() {
      this.saveLoading = true;

      // 收集已入位的数据
      const result = [];
      for (let i = 0; i < this.pathCells.length; i++) {
        if (this.pathCells[i].person) {
          result.push({
            position: i + 1, // 位置编号
            personId: this.pathCells[i].person.id,
            personName: this.pathCells[i].person.name
          });
        }
      }

      // 保存数据包括分类信息
      const saveData = {
        category: this.currentCategory,
        categoryLabel: this.getCurrentCategoryLabel(),
        totalPersons: this.totalPersons,
        placedCount: result.length,
        result: result
      };

      console.log("保存抽签结果：", saveData);

      // 模拟保存到服务端
      setTimeout(() => {
        this.saveLoading = false;
        this.$message.success(
          `保存成功！${this.getCurrentCategoryLabel()} - 已保存${
            result.length
          }人的抽签结果`
        );

        // TODO: 实际调用服务端API
        // this.$http.post('/api/lottery/save', saveData).then(response => {
        //   this.saveLoading = false;
        //   this.$message.success('保存成功！');
        // }).catch(error => {
        //   this.saveLoading = false;
        //   this.$message.error('保存失败：' + error.message);
        // });
      }, 1000);
    }
  }
};
</script>

<style scoped>
.draw-lottery-container {
  height: 92vh;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  padding: 10px;
  overflow: hidden;
  display: flex;
  align-items: center;
  justify-content: center;
}

/* 主内容区 - 左右布局 */
.main-content {
  display: flex;
  gap: 15px;
  width: 100%;
  height: calc(92vh - 20px);
}

/* 左侧面板 - 游戏区域 */
.left-panel {
  flex: 1;
  display: flex;
  flex-direction: column;
  min-width: 0;
  overflow: hidden;
  padding: 10px;
  gap: 10px;
}

/* 分类选择器 */
.category-selector {
  background: rgba(255, 255, 255, 0.95);
  border-radius: 10px;
  padding: 10px 15px;
  display: flex;
  align-items: center;
  gap: 15px;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.15);
  flex-shrink: 0;
}

.category-select {
  width: 160px;
}

.category-info {
  display: flex;
  gap: 15px;
  flex: 1;
}

.info-text {
  color: #606266;
  font-size: 14px;
  font-weight: 500;
  padding: 4px 12px;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  background-clip: text;
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
}

.exit-button {
  margin-left: auto;
  font-weight: 600;
  border-radius: 6px;
  box-shadow: 0 2px 8px rgba(255, 73, 73, 0.3);
  transition: all 0.3s;
}

.exit-button:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(255, 73, 73, 0.5);
}

/* 右侧面板 - 控制区域 */
.right-panel {
  width: 280px;
  display: flex;
  flex-direction: column;
  gap: 10px;
}

/* 头部紧凑版 */
.header-compact {
  background: rgba(255, 255, 255, 0.95);
  border-radius: 10px;
  padding: 12px 15px;
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.2);
}

.header-compact h2 {
  font-size: 20px;
  margin: 0 0 8px 0;
  color: #667eea;
  text-align: center;
}

.stats-compact {
  display: flex;
  justify-content: center;
  gap: 10px;
}

.stat-badge {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  padding: 4px 12px;
  border-radius: 12px;
  font-size: 13px;
}

.stat-badge strong {
  color: #ffd700;
  font-size: 16px;
}

/* 控制按钮紧凑版 */
.controls-compact {
  display: flex;
  gap: 8px;
}

.btn-compact {
  flex: 1;
  padding: 10px;
  font-size: 14px;
  font-weight: 600;
  border: none;
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.3s;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.2);
  color: white;
}

.btn-compact.btn-draw {
  background: linear-gradient(135deg, #f093fb 0%, #f5576c 100%);
}

.btn-compact.btn-auto {
  background: linear-gradient(135deg, #4facfe 0%, #00f2fe 100%);
}

.btn-compact.btn-save {
  background: linear-gradient(135deg, #fa709a 0%, #fee140 100%);
}

.btn-compact:hover:not(:disabled) {
  transform: translateY(-2px);
}

.btn-compact:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

/* 覆盖 el-button 默认样式 */
.btn-compact.el-button {
  flex: 1;
  padding: 10px;
  font-size: 14px;
  font-weight: 600;
  border: none;
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.3s;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.2);
  color: white;
}

.btn-compact.btn-draw.el-button--primary {
  background: linear-gradient(135deg, #f093fb 0%, #f5576c 100%);
  border: none;
}

.btn-compact.btn-auto.el-button--success {
  background: linear-gradient(135deg, #4facfe 0%, #00f2fe 100%);
  border: none;
}

.btn-compact.btn-save.el-button--warning {
  background: linear-gradient(135deg, #fa709a 0%, #fee140 100%);
  border: none;
}

.btn-compact.el-button:hover:not(.is-disabled) {
  transform: translateY(-2px);
  opacity: 0.9;
}

.btn-compact.el-button.is-disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

/* el-button loading 图标颜色 */
.btn-compact.el-button .el-icon-loading {
  color: white;
}

/* 当前抽中的人 - 紧凑版 */
.current-draw-compact {
  flex: 1;
  display: flex;
  flex-direction: column;
}

.draw-card-compact {
  background: white;
  border-radius: 10px;
  padding: 15px;
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.2);
  height: 100%;
  display: flex;
  flex-direction: column;
}

.card-header-compact {
  font-size: 16px;
  font-weight: 600;
  color: #667eea;
  text-align: center;
  margin-bottom: 12px;
}

.card-body-compact {
  display: flex;
  align-items: center;
  gap: 12px;
  flex: 1;
}

/* 紧凑版头像 */
.person-avatar-compact {
  width: 50px;
  height: 50px;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 20px;
  font-weight: 700;
  flex-shrink: 0;
  transition: all 0.3s;
}

.person-avatar-compact.empty {
  background: linear-gradient(135deg, #ccc 0%, #999 100%);
  font-size: 28px;
}

.person-avatar-compact.rolling {
  animation: rolling-pulse 0.3s infinite alternate;
  background: linear-gradient(135deg, #f093fb 0%, #f5576c 100%);
}

@keyframes rolling-pulse {
  0% {
    transform: scale(1);
  }
  100% {
    transform: scale(1.1);
  }
}

/* 动画开关 */
.animation-toggle {
  background: rgba(255, 255, 255, 0.95);
  border-radius: 10px;
  padding: 15px;
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.2);
}

.toggle-label {
  display: flex;
  align-items: center;
  cursor: pointer;
  user-select: none;
  gap: 10px;
}

.toggle-checkbox {
  display: none;
}

.toggle-slider {
  position: relative;
  width: 50px;
  height: 26px;
  background: #ccc;
  border-radius: 13px;
  transition: background 0.3s;
  flex-shrink: 0;
}

.toggle-slider::before {
  content: "";
  position: absolute;
  width: 22px;
  height: 22px;
  border-radius: 50%;
  background: white;
  top: 2px;
  left: 2px;
  transition: transform 0.3s;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
}

.toggle-checkbox:checked + .toggle-slider {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
}

.toggle-checkbox:checked + .toggle-slider::before {
  transform: translateX(24px);
}

.toggle-text {
  font-size: 14px;
  font-weight: 600;
  color: #333;
  transition: color 0.3s;
}

.toggle-checkbox:checked ~ .toggle-text {
  color: #667eea;
}

/* 紧凑版人员信息 */
.person-info-compact {
  flex: 1;
  text-align: left;
}

.person-name-compact {
  font-size: 16px;
  font-weight: 600;
  color: #333;
  margin-bottom: 4px;
  transition: all 0.2s;
}

.person-name-compact.rolling-text {
  color: #f5576c;
  animation: text-flash 0.3s infinite alternate;
}

.person-position-compact {
  font-size: 13px;
  color: #999;
  transition: all 0.2s;
}

.person-position-compact.rolling-text {
  color: #f093fb;
  animation: text-flash 0.3s infinite alternate;
}

@keyframes text-flash {
  0% {
    opacity: 0.7;
  }
  100% {
    opacity: 1;
  }
}

/* 跑马灯状态下的卡片 */
.draw-card-compact.is-rolling {
  animation: card-shake 0.5s infinite;
  box-shadow: 0 4px 20px rgba(245, 87, 108, 0.5);
}

@keyframes card-shake {
  0%,
  100% {
    transform: translateX(0);
  }
  25% {
    transform: translateX(-3px);
  }
  75% {
    transform: translateX(3px);
  }
}

/* 待抽签列表 - 紧凑版 */
.waiting-list-compact {
  background: rgba(255, 255, 255, 0.95);
  border-radius: 10px;
  padding: 12px 15px;
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.2);
  max-height: 200px;
  overflow-y: auto;
}

.waiting-list-compact h3 {
  color: #667eea;
  margin: 0 0 10px 0;
  font-size: 15px;
  text-align: center;
}

.person-chips-compact {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(40px, 1fr));
  gap: 6px;
}

.person-chip-compact {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  padding: 6px;
  border-radius: 8px;
  font-size: 12px;
  font-weight: 600;
  text-align: center;
  box-shadow: 0 2px 6px rgba(102, 126, 234, 0.3);
  transition: all 0.3s;
}

.person-chip-compact:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 10px rgba(102, 126, 234, 0.5);
}

.more-indicator {
  background: linear-gradient(135deg, #fa709a 0%, #fee140 100%);
  color: white;
  padding: 6px;
  border-radius: 8px;
  font-size: 12px;
  font-weight: 600;
  text-align: center;
  grid-column: span 2;
}

.maze-container {
  position: relative;
  flex: 1;
  width: 100%;
  min-height: 0;
  background: rgba(255, 255, 255, 0.95);
  border-radius: 15px;
  padding: 10px;
  box-sizing: border-box;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.2);
  overflow-y: auto;
  overflow-x: hidden;
  background-image: linear-gradient(
      45deg,
      #f5f7fa 25%,
      transparent 25%,
      transparent 75%,
      #f5f7fa 75%,
      #f5f7fa
    ),
    linear-gradient(
      45deg,
      #f5f7fa 25%,
      transparent 25%,
      transparent 75%,
      #f5f7fa 75%,
      #f5f7fa
    );
  background-size: 10px 10px;
  background-position: 0 0, 5px 5px;
}

/* el-row 间距调整 */
.maze-container .el-row {
  margin-bottom: 0 !important;
}

/* 路径格子 */
.path-cell {
  background: white;
  border: 1px solid #e0e0e0;
  border-radius: 3px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  transition: all 0.3s;
  cursor: pointer;
  box-sizing: border-box;
  overflow: hidden;
  width: 100%;
  padding-top: 50%; /* 减小高度为宽度的50% */
  position: relative;
  min-height: 25px;
}

.path-cell > * {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  width: 100%;
}

.path-cell:hover {
  transform: scale(1.05);
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
  z-index: 10;
}

.path-cell.is-occupied {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  border-color: #667eea;
  color: white;
}

.path-cell.is-start {
  background: linear-gradient(135deg, #11998e 0%, #38ef7d 100%);
  border-color: #11998e;
}

.path-cell.is-end {
  background: linear-gradient(135deg, #f093fb 0%, #f5576c 100%);
  border-color: #f5576c;
}

.path-cell.is-corner {
  border-width: 2px;
  border-color: #ffd700;
}

.cell-number {
  font-size: 8px;
  color: #999;
  position: absolute;
  top: 2px;
  left: 2px;
  line-height: 1;
  transform: none !important;
  width: auto !important;
}

.path-cell.is-occupied .cell-number {
  color: rgba(255, 255, 255, 0.6);
}

.person-in-cell {
  text-align: center;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  width: 100%;
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%) !important;
}

.person-id {
  font-size: 12px;
  font-weight: 700;
  margin-bottom: 2px;
  line-height: 1.2;
}

.person-name-small {
  font-size: 9px;
  opacity: 0.9;
  line-height: 1.2;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 90%;
}

/* 动画中的人物 */
.animating-person {
  position: fixed;
  width: 100px;
  height: 100px;
  z-index: 1000;
  pointer-events: none;
}

.person-avatar-large {
  width: 100px;
  height: 100px;
  background: linear-gradient(135deg, #f093fb 0%, #f5576c 100%);
  color: white;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 36px;
  font-weight: 700;
  box-shadow: 0 10px 30px rgba(240, 147, 251, 0.5);
  margin-bottom: 10px;
}

.animating-person .person-name {
  text-align: center;
  font-size: 16px;
  font-weight: 600;
  color: white;
  text-shadow: 2px 2px 4px rgba(0, 0, 0, 0.3);
}
</style>



// WEBPACK FOOTER //
// src/view/newsGlIndex/DrawLottery.vue